import { 
    type ProgramGuideItem, 
    type OnDemandContent, 
    type ProgramGuide, 
    OnDemandData, 
    type Channel,
    ItemPaging,
    isFizzTV,
    type OnDemandItem,
    CreditsPage,
    ChannelsContent,
    type Credit,
    LangPopularWords
} from '@ca.vmedia.tv/domains';

import { ProgramCardData, OnDemandCardData, ChannelLiveCardData, CreditCardData, type ShowAllCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import { Services } from "@ca.vmedia.tv/services";
import { Logger, TimeUtils } from '@/utils';
import { ShowAllContentType } from "@/components/Header/types/LinkItemsCollection";
import { useExtraCards } from "@/composables/useExtraCards";
import { useChannelsStore } from "@/stores/Channels";
import { Strings } from "@/locales/types/enums";
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { AxiosError } from 'axios';
import type { ExposeInterface } from './types';
import { LangCodes } from "@/common/types/languages";

export const useSearchPageStore = defineStore('SearchPage', (): ExposeInterface => {
    const { 
        emptyOnDemandContent, 
        emptyChannelsContent, 
        emptyCreditsPage,
        emptyOnDemandData
    } = useEmptyLine();

    const { getFilteredChannels } = useChannelsStore();

    const { hasShowAll, getCardShowAll } = useExtraCards();

    const { $i18n, $analytics } = useNuxtApp();

    const getChannelsTitle = computed((): string => $i18n.t(Strings.SEARCH_CHANNELS).toString());
    const getLiveTitle = computed((): string => $i18n.t(Strings.SEARCH_LIVE_TV).toString());
    const getMoviesTitle = computed((): string => isFizzTV ? $i18n.t(Strings.SEARCH_MOVIES_ON_DEMAND_FIZZ).toString() : $i18n.t(Strings.SEARCH_MOVIES_ON_DEMAND).toString());
    const getShowsTitle = computed((): string => isFizzTV ? $i18n.t(Strings.SEARCH_TV_SHOWS_ON_DEMAND_FIZZ).toString() : $i18n.t(Strings.SEARCH_TV_SHOWS_ON_DEMAND).toString());
    const getCreditsTitle = computed((): string => $i18n.t(Strings.SEARCH_CAST_AND_CREW).toString());

    const _isLoading: Ref<boolean> = ref(false);
    const getIsLoading: ComputedRef<boolean> = computed(() => _isLoading.value);
    const _setIsLoading = (isLoading: boolean) => {
        _isLoading.value = isLoading;
    };

    const _query: Ref<string> = ref('');
    const getQuery: ComputedRef<string> = computed(() => _query.value || '');
    const clearQuery = (): void => {
        _query.value = '';
    };
    const setQuery = (query: string): void => {
        _query.value = query;
        if (query) {
            $analytics.createEventSearch(query);
        }        
    };

    const _popularWords: Ref<string[]> = ref([]);
    const getPopularWords: ComputedRef<string[]> = computed(() => _popularWords.value);
    const clearPopularWords = (): void => {
        _popularWords.value = [];
    };
    const fetchPopularWords = async (query: string): Promise<void> => {
        try {
            const lang: number = $i18n.locale.value === LangCodes.EN ? LangPopularWords.en : LangPopularWords.fr;
            const response = await Services.Instance.search.getPopularSearchWords(query, 5, lang);
            _popularWords.value = response.Result || [];
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    }

    const _moviesResult: Ref<OnDemandContent | null> = ref(null);
    const fetchMoviesResult = async (): Promise<void> => {
        try {
            if (!getQuery.value){
                return;
            }

            _setIsLoading(true);
            _moviesResult.value = await Services.Instance.vod.searchMovies(
                getQuery.value,
                1,
                AppConstants.ItemsInLine,
            );
            
        } catch (error) {
            _moviesResult.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoading(false);
        }
    };
    const getCardsMoviesResult: ComputedRef<(OnDemandCardData | ShowAllCardData)[] | null> = computed(() => {
        const data = _moviesResult.value;
        
        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    }); 

    const _showsResult: Ref<OnDemandContent | null> = ref(null);
    const fetchShowsResult = async (): Promise<void> => {
        try {
            if (!getQuery.value){
                return;
            }

            _setIsLoading(true);
            _showsResult.value = await Services.Instance.vod.searchShows(
                getQuery.value,
                1,
                AppConstants.ItemsInLine,
            );
        } catch (error) {
            _showsResult.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoading(false);
        }
    };
    const getCardsShowsResult: ComputedRef<(OnDemandCardData | ShowAllCardData)[] | null> = computed(() => {
        const data = _showsResult.value;
        
        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

   
    const _channelsResult: Ref<ChannelsContent | null> = ref(null);
    const fetchChannelsResult = async (): Promise<void> => {
        try {
            if (!getQuery.value){
                return;
            }

            _setIsLoading(true);

            const availableChannels = getFilteredChannels(getQuery.value);
            
            const items: Channel[] = availableChannels.slice(0, AppConstants.ItemsInLine);
            const paging = new ItemPaging(
                AppConstants.ItemsInLine,
                1,
                availableChannels.length,
                Math.ceil(availableChannels.length / AppConstants.ItemsInLine),
            );

            _channelsResult.value = new ChannelsContent(items, null, null, paging);
        } catch (error) {
            _channelsResult.value = emptyChannelsContent();
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoading(false);
        }
    };
    const getCardsChannelsResult: ComputedRef<(ChannelLiveCardData | ShowAllCardData)[] | null> = computed(()  => {
        const data = _channelsResult.value;
        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new ChannelLiveCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        return items;
    });

    const _creditsResult: Ref<CreditsPage | null> = ref(null);
    const fetchCreditsResult = async (): Promise<void> => {
        try {
            if (!getQuery.value){
                return;
            }

            _setIsLoading(true);

            _creditsResult.value = await Services.Instance.credits.searchCredits(
                getQuery.value, 
                1, 
                AppConstants.ItemsInLine
            );
        } catch (error) {
            _creditsResult.value = emptyCreditsPage();
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoading(false);
        }
    };
    const getCardsCreditsResult: ComputedRef<(CreditCardData | ShowAllCardData)[] | null> = computed(() => {
        const data = _creditsResult.value;
        
        if (data === null || !data.items) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new CreditCardData(item));
        
        if (!!data.paging && hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const liveSearchDate = TimeUtils.dateNow();
    const _liveResult: Ref<OnDemandData | null> = ref(null);
    const fetchLiveResult = async (): Promise<void> => {
        try {
            if (!getQuery.value){
                return;
            }
            _setIsLoading(true);
            
            const dateFrom = TimeUtils.getMoment($i18n.locale, liveSearchDate).add(-7, "days").valueOf();
            const dateTill = TimeUtils.getMoment($i18n.locale, liveSearchDate).add(7, "days").valueOf();

            const data = await Services.Instance.guide.searchLivePrograms(
                {
                    searchText: getQuery.value,
                    dateFrom,
                    dateTill,
                },
                AppConstants.ItemsInLine,
                1
            );

            const items = data.items ?? [];
            _liveResult.value = new OnDemandData(items, null, data.paging);
        } catch (error) {
            _liveResult.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoading(false);
        }
    };
    const getCardsLiveResult: ComputedRef<(ProgramCardData | ShowAllCardData)[] | null> = computed(() => {
        const data = _liveResult.value;
        
        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new ProgramCardData(item as unknown as ProgramGuideItem));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });


    const getHasResult: ComputedRef<boolean | null> = computed(() => {
        return getCardsMoviesResult.value && getCardsMoviesResult.value.length > 0 ||
            getCardsShowsResult.value && getCardsShowsResult.value.length > 0 ||
            getCardsChannelsResult.value && getCardsChannelsResult.value.length > 0 ||
            getCardsCreditsResult.value && getCardsCreditsResult.value.length > 0 ||
            getCardsLiveResult.value && getCardsLiveResult.value.length > 0;
    });

    const _typePage: Ref<string | null> = ref(null);
    const getTypesPage: ComputedRef<string | null> = computed(() => _typePage.value);
    const _showAllItems: Ref<CreditsPage | ChannelsContent | ProgramGuide | OnDemandData | null> = ref(null);
    const getShowAllCards: ComputedRef<(ProgramCardData | OnDemandCardData | CreditCardData | ChannelLiveCardData)[] | null> = computed(() => {
        if (_showAllItems.value === null) {
            return null;
        }

        switch (_typePage.value) {
            case ShowAllContentType.Movies:
                return (_showAllItems.value as OnDemandData).items?.map((item) => new OnDemandCardData(item)) ?? null;

            case ShowAllContentType.Shows:
                return (_showAllItems.value as OnDemandData).items?.map((item) => new OnDemandCardData(item)) ?? null;

            case ShowAllContentType.Credits:
                return (_showAllItems.value as CreditsPage).items?.map((item) => new CreditCardData(item)) ?? null;

            case ShowAllContentType.Channels:
                return (_showAllItems.value as ChannelsContent).items?.map((item) => new ChannelLiveCardData(item)) ?? null;

            case ShowAllContentType.Live:
                return (_showAllItems.value as ProgramGuide).items?.map((item) => new ProgramCardData(item)) ?? null;
            
            default:
                return [];
        }
    });
    const clearShowAllSearch = (): void => {
        _showAllItems.value = null;
        _typePage.value = null;
    };
    const setShowAllSearch = (type: string): void => {
        try {
            if (_showAllItems.value !== null) {
                return;
            };

            _typePage.value = type;

            switch (type) {
                case ShowAllContentType.Movies:
                    _showAllItems.value = new OnDemandData(_moviesResult.value?.items, null, _moviesResult.value?.paging);
                    break;

                case ShowAllContentType.Shows:
                    _showAllItems.value = new OnDemandData(_showsResult.value?.items, null, _showsResult.value?.paging);
                    break;

                case ShowAllContentType.Live:
                    _showAllItems.value = new OnDemandData(_liveResult.value?.items, null, _liveResult.value?.paging);
                    break;

                case ShowAllContentType.Credits:
                    if (!_creditsResult.value) {
                        Logger.captureError(new Error('Credits result is null'));
                        return;
                    }
                    _showAllItems.value = new CreditsPage(_creditsResult.value.items, _creditsResult.value.paging);
                    break;

                case ShowAllContentType.Channels:
                    _showAllItems.value = new ChannelsContent(_channelsResult.value?.items, null, null, _channelsResult.value?.paging);
                    break;
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllMoviesNextPage = async (pageNumber: number): Promise<void> => {
        try {
            const data = await Services.Instance.vod.searchMovies(
                getQuery.value,
                pageNumber,
                AppConstants.ItemsInLine,
            );
    
            if (_showAllItems.value && data.items !== null && data.items.length != 0) {
                const items = (_showAllItems.value.items as OnDemandItem[]).concat(data.items);
                _showAllItems.value = new OnDemandData(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllShowsNextPage = async (pageNumber: number): Promise<void> => {
        try {
            const data = await Services.Instance.vod.searchShows(
                getQuery.value,
                pageNumber,
                AppConstants.ItemsInLine,
            );
    
            if (_showAllItems.value && data.items !== null && data.items.length != 0) {
                const items = (_showAllItems.value.items as OnDemandItem[]).concat(data.items);
                _showAllItems.value = new OnDemandData(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllCreditsNextPage = async (pageNumber: number): Promise<void> => {
        try {
            const data = await Services.Instance.credits.searchCredits(
                getQuery.value,
                pageNumber,
                AppConstants.ItemsInLine,
            );

            if (_showAllItems.value && !!data.items && data.items.length != 0 && _showAllItems.value.paging !== null) {
                const items = (_showAllItems.value.items as Credit[]).concat(data.items);
                _showAllItems.value = new CreditsPage(items, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllLiveNextPage = async (pageNumber: number): Promise<void> => {
        try {
            const dateFrom = TimeUtils.getMoment($i18n.locale, liveSearchDate).add(-7, "days").valueOf();
            const dateTill = TimeUtils.getMoment($i18n.locale, liveSearchDate).add(7, "days").valueOf();

            const data = await Services.Instance.guide.searchLivePrograms(
                {
                    searchText: getQuery.value,
                    dateFrom,
                    dateTill,
                },
                AppConstants.ItemsInLine,
                pageNumber
            );
    
            if (_showAllItems.value && data && data.items && data.items.length != 0) {
                const items = (_showAllItems.value.items as OnDemandItem[]).concat(data.items);
                _showAllItems.value = new OnDemandData(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllChannelsNextPage = async (pageNumber: number): Promise<void> => {
        try {
            const availableChannels = getFilteredChannels(getQuery.value);
            const itemsChannels: Channel[] = availableChannels.slice((pageNumber - 1) * AppConstants.ItemsInLine, pageNumber * AppConstants.ItemsInLine);

            if (_showAllItems.value && itemsChannels.length != 0) {
                const items = (_showAllItems.value.items as Channel[]).concat(itemsChannels);
                _showAllItems.value = new ChannelsContent(items, null, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const loadShowAllNextPage = async (): Promise<void> => {
        if(_isLoading.value === true){
            return;
        }

        const pageNumber = !_showAllItems.value?.items || !_showAllItems.value.paging?.itemsOnPage
            ? -1
            : Math.ceil(_showAllItems.value.items.length / _showAllItems.value.paging.itemsOnPage) + 1;
        if(pageNumber < 0 || !_showAllItems.value?.paging?.totalPages || pageNumber > _showAllItems.value.paging?.totalPages){
            return;
        }

        try {
            _setIsLoading(true);
            
            switch (_typePage.value) {
                case ShowAllContentType.Movies:
                    await _loadShowAllMoviesNextPage(pageNumber);
                    break;

                case ShowAllContentType.Shows:
                    await _loadShowAllShowsNextPage(pageNumber);
                    break;

                case ShowAllContentType.Credits:
                    await _loadShowAllCreditsNextPage(pageNumber);
                    break;

                case ShowAllContentType.Live:
                    await _loadShowAllLiveNextPage(pageNumber);
                    break;
                
                case ShowAllContentType.Channels:
                    await _loadShowAllChannelsNextPage(pageNumber);
                    break;
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoading(false);
        }
    };

    const _counterRows: Ref<number> = ref(AppConstants.SearchCounterLines);
    const getCounterLinesSearch: ComputedRef<number> = computed(() => _counterRows.value);
    const _clearCounter = (): void => {
        _counterRows.value = AppConstants.SearchCounterLines;
    };
    const increaseCounterLinesSearch = (): void => {
        _counterRows.value += 1;
    };

    const clearAllSearchPage = (): void => {
        _clearCounter();
        clearShowAllSearch();
        _moviesResult.value = null;
        _showsResult.value = null;
        _channelsResult.value = null;
        _creditsResult.value = null;
        _liveResult.value = null;
        clearQuery();
    };

    return {
        getQuery,
        setQuery,
        fetchMoviesResult,
        getCardsMoviesResult,
        fetchShowsResult,
        getCardsShowsResult,
        fetchChannelsResult,
        getCardsChannelsResult,
        getCounterLinesSearch,
        increaseCounterLinesSearch,
        fetchCreditsResult,
        getCardsCreditsResult,
        fetchLiveResult,
        getCardsLiveResult,
        clearAllSearchPage,
        getHasResult,
        getIsLoading,
        loadShowAllNextPage,
        getShowAllCards,
        setShowAllSearch,
        clearShowAllSearch,
        getTypesPage,
        getChannelsTitle,
        getLiveTitle,
        getMoviesTitle,
        getShowsTitle,
        getCreditsTitle,
        clearQuery,
        fetchPopularWords,
        getPopularWords,
        clearPopularWords
    };
});